import { push } from 'connected-react-router';
import * as React from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import {
	commitAeMaxPriceUpdate,
	createAeMaxPriceExportXls,
	ErrorResponse as AeMaxPriceErrorResponse,
	handleAeMaxPriceUpdateResponse,
	selectExport as selectAeMaxPriceExport,
} from '~/AeMaxPrice';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { ErrorResponse as CzMaxPriceErrorResponse } from '~/Api/User/ProductGroup/CzMaxPrice';
import { ErrorResponse as CzReimbursementErrorResponse } from '~/Api/User/ProductGroup/CzReimbursement';
import { ErrorResponse as HrMaxPriceErrorResponse } from '~/Api/User/ProductGroup/HrMaxPrice';
import { ErrorResponse as RoMaxPriceErrorResponse } from '~/Api/User/ProductGroup/RoMaxPrice';
import { ErrorResponse as SkMaxPriceErrorResponse } from '~/Api/User/ProductGroup/SkMaxPrice';
import {
	commitCzMaxPriceUpdate,
	createCzMaxPriceExportXls,
	handleCzMaxPriceUpdateResponse,
	selectExport as selectCzMaxPriceExport,
} from '~/CzMaxPrice';
import {
	commitCzReimbursementUpdate,
	createCzReimbursementExportXls,
	handleCzReimbursementUpdateResponse,
	selectExport as selectCzReimbursementExport,
} from '~/CzReimbursement';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage, removeFlashMessageByText } from '~/FlashMessage';
import { logGoogleTagManagerGroupExport } from '~/GoogleTagManager';
import {
	commitHrMaxPriceUpdate,
	createHrMaxPriceExportXls,
	handleHrMaxPriceUpdateResponse,
	selectExport as selectHrMaxPriceExport,
} from '~/HrMaxPrice';
import { createEvidenceExport, createGroupExportXmlUrl } from '~/ProductGroup';
import {
	commitRoMaxPriceUpdate,
	createExport as createRoMaxPriceExport,
	ExportType as RoMaxPriceExportType,
	handleRoMaxPriceUpdateResponse,
	selectExport as selectRoMaxPriceExport,
} from '~/RoMaxPrice';
import {
	commitSiMaxPriceUpdate,
	createSiMaxPriceExportXls,
	ErrorResponse as SiMaxPriceErrorResponse,
	handleSiMaxPriceUpdateResponse,
	selectExport as selectSiMaxPriceExport,
} from '~/SiMaxPrice';
import {
	commitSkMaxPriceUpdate,
	createSkMaxPriceExportCNRefXls,
	createSkMaxPriceExportCNRefPzluXls,
	createSkMaxPriceExportRawXls,
	handleSkMaxPriceUpdateResponse,
	selectExport as selectSkMaxPriceExport,
} from '~/SkMaxPrice';
import { State, ThunkAction, ThunkDispatch } from '~/Store';
import { formatCountriesError } from '~/UI';
import * as downlaodFlashMessages from './DownloadFlashMessages';

import DownloadPopover from './DownloadPopover';
import { buildErrorLocation, processExport } from './productGroupDownloadHelpers';

const mapStateToProps = (state: State, props: { group: UserProductGroup }) => {
	const { accessToken } = state.accessToken;

	const downloads = accessToken
		? [
				selectCzMaxPriceExport(state.czMaxPrice, props.group.id),
				selectSkMaxPriceExport(state.skMaxPrice, props.group.id),
				selectCzReimbursementExport(state.czReimbursement, props.group.id),
				selectAeMaxPriceExport(state.aeMaxPrice, props.group.id),
				selectSiMaxPriceExport(state.siMaxPrice, props.group.id),
				selectHrMaxPriceExport(state.hrMaxPrice, props.group.id),
				selectRoMaxPriceExport(state.roMaxPrice, props.group.id),
				state.productGroup.exportRequest ? { isFetching: true } : null,
		  ]
		: [];

	return {
		canExportEvidence: accessToken?.subscription?.systemModules.has('product-group-export-evidence') ?? false,
		isFetching: downloads.find((download) => download && download.isFetching) !== undefined,
	};
};

const mapDispatchToProps = (
	dispatch: ThunkDispatch<AnyAction>,
	props: { group: UserProductGroup }
): {
	onProductsExport: (currency: string | null) => Promise<void>;
	onEvidenceExport: () => Promise<void>;
	onCzMaxPriceExport: () => any;
	onCzReimbursementExport: () => any;
	onSkMaxPriceCNRefExport: () => any;
	onSkMaxPriceCNRefPzluExport: () => any;
	onSkMaxPriceRawExport: () => any;
	onAeMaxPriceExport: () => Promise<void>;
	onSiMaxPriceExport: () => Promise<void>;
	onHrMaxPriceExport: () => Promise<void>;
	onRoMaxPriceExport: (type: RoMaxPriceExportType) => Promise<void>;
} => {
	return {
		onProductsExport: async (currency: string | null) => {
			logGoogleTagManagerGroupExport(props.group.id);
			const response = await dispatch(createGroupExportXmlUrl(props.group.id, currency));
			if (response) {
				dispatch(processExport(response.export));
			}
		},
		onEvidenceExport: async () => {
			const response = await dispatch(createEvidenceExport(props.group.id));
			if (response) {
				dispatch(processExport(response.export));
			}
		},
		onCzMaxPriceExport: () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingPackageSizeErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));

			return dispatch(commitCzMaxPriceUpdate(props.group.id))
				.then(() => dispatch(createCzMaxPriceExportXls(props.group.id)))
				.then((response) => dispatch(processExport(response.export)))
				.catch((response) => {
					if (!(response instanceof CzMaxPriceErrorResponse)) {
						dispatch(handleError(response));
						return;
					}

					const validationResult = response.toValidationResult();
					const errorLocation =
						dispatch(buildErrorLocation(validationResult.productErrors)) +
						formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

					if (response.handleCalculationRequiresAtLeastOneReferenceCountryProduct()) {
						dispatch(
							flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation)
						);
					}
					if (response.handleCalculationRequiresAtMostOneProductForEachCountry()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresReferenceSettings()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingPackageSizeErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiredProductSettingsIsMissing()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
					}
					if (response.handleIllegalPriceTypeSelected()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
					}
					if (response.handleMissingThreeReferenceCountryProductsErrorCode()) {
						dispatch(
							flashErrorMessage(downlaodFlashMessages.MissingThreeReferenceCountryProductsMessage + errorLocation)
						);
					}

					dispatch(handleCzMaxPriceUpdateResponse(response, props.group.id));
				});
		},
		onCzReimbursementExport: () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingCzReimbursementReferenceErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));

			return dispatch(commitCzReimbursementUpdate(props.group.id))
				.then(() => dispatch(createCzReimbursementExportXls(props.group.id)))
				.then((response) => dispatch(processExport(response.export)))
				.catch((response) => {
					if (!(response instanceof CzReimbursementErrorResponse)) {
						dispatch(handleError(response));
						return;
					}

					const validationResult = response.toValidationResult();
					if (validationResult.groupId !== null) {
						dispatch(push(`/product-groups/${validationResult.groupId}`));
					}

					const errorLocation =
						dispatch(buildErrorLocation(validationResult.productErrors)) +
						formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

					if (response.handleCalculationRequiresAtLeastOneReferenceCountryProduct()) {
						dispatch(
							flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation)
						);
					}
					if (response.handleCalculationRequiresAtLeastOneProduct()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresAtMostOneProductForEachCountry()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresReferenceSettings()) {
						dispatch(
							flashErrorMessage(downlaodFlashMessages.MissingCzReimbursementReferenceErrorMessage + errorLocation)
						);
					}
					if (response.handleCalculationRequiredProductSettingsIsMissing()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
					}
					if (response.handleIllegalPriceTypeSelected()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresAtLeastOneProductList()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
					}

					dispatch(handleCzReimbursementUpdateResponse(response, props.group.id));
				});
		},
		onSkMaxPriceCNRefExport: () => handleSkMaxPriceExport(createSkMaxPriceExportCNRefXls, dispatch, props),
		onSkMaxPriceCNRefPzluExport: () => handleSkMaxPriceExport(createSkMaxPriceExportCNRefPzluXls, dispatch, props),
		onSkMaxPriceRawExport: () => handleSkMaxPriceExport(createSkMaxPriceExportRawXls, dispatch, props),
		onAeMaxPriceExport: async () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));

			try {
				await dispatch(commitAeMaxPriceUpdate(props.group.id));
				const response = await dispatch(createAeMaxPriceExportXls(props.group.id));
				dispatch(processExport(response.export));
			} catch (error) {
				if (!(error instanceof AeMaxPriceErrorResponse)) {
					dispatch(handleError(error));
					return;
				}

				const validationResult = error.toValidationResult();
				const errorLocation =
					dispatch(buildErrorLocation(validationResult.productErrors)) +
					formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

				if (error.handleProductSettingsMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
				}
				if (error.handleReferenceCountryProductMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation));
				}
				if (error.handleAtMostOneProductPerCountry()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
				}
				if (error.handleReferenceSettingsMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
				}
				if (error.handleIllegalPriceTypeSelected()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
				}
				dispatch(handleAeMaxPriceUpdateResponse(error, props.group.id));
			}
		},
		onSiMaxPriceExport: async () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));

			try {
				await dispatch(commitSiMaxPriceUpdate(props.group.id));
				const response = await dispatch(createSiMaxPriceExportXls(props.group.id));
				dispatch(processExport(response.export));
			} catch (error) {
				if (!(error instanceof SiMaxPriceErrorResponse)) {
					dispatch(handleError(error));
					return;
				}

				const validationResult = error.toValidationResult();
				const errorLocation =
					dispatch(buildErrorLocation(validationResult.productErrors)) +
					formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

				if (error.handleRequiredProductSettingsMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
				}
				if (error.handleRequiresMinOneEuropeanProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation));
				}
				if (error.handleRequiresMaxOneEuropeanProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
				}
				if (error.handleRequiresReferenceSettingsErrorCode()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
				}
				if (error.handleIllegalPriceTypeSelected()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
				}
				if (error.handleExportB1RequiresReferenceCountries()) {
					dispatch(
						flashErrorMessage(downlaodFlashMessages.ExportB1RequiresReferenceCountriesErrorMessage + errorLocation)
					);
				}
				dispatch(handleSiMaxPriceUpdateResponse(error, props.group.id));
			}
		},
		onHrMaxPriceExport: async () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));

			try {
				await dispatch(commitHrMaxPriceUpdate(props.group.id));
				const response = await dispatch(createHrMaxPriceExportXls(props.group.id));
				dispatch(processExport(response.export));
			} catch (error) {
				if (!(error instanceof HrMaxPriceErrorResponse)) {
					dispatch(handleError(error));
					return;
				}

				const validationResult = error.toValidationResult();
				const errorLocation =
					dispatch(buildErrorLocation(validationResult.productErrors)) +
					formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

				if (error.handleRequiredProductSettingsMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
				}
				if (error.handleRequiresMinOneReferenceProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation));
				}
				if (error.handleRequiresMaxOneReferenceProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
				}
				if (error.handleRequiresReferenceSettingsErrorCode()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
				}
				if (error.handleIllegalPriceTypeSelected()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
				}
				if (error.handleExportRequiresReferenceCountries()) {
					dispatch(
						flashErrorMessage(downlaodFlashMessages.ExportB1RequiresReferenceCountriesErrorMessage + errorLocation)
					);
				}
				dispatch(handleHrMaxPriceUpdateResponse(error, props.group.id));
			}
		},
		onRoMaxPriceExport: async (type: RoMaxPriceExportType) => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.RequiresAtMostOneRomanianProductForEachDatasource));

			try {
				await dispatch(commitRoMaxPriceUpdate(props.group.id));
				const response = await dispatch(createRoMaxPriceExport(props.group.id, type));
				dispatch(processExport(response.export));
			} catch (error) {
				if (!(error instanceof RoMaxPriceErrorResponse)) {
					dispatch(handleError(error));
					return;
				}

				const validationResult = error.toValidationResult();
				if (validationResult.groupId !== null) {
					dispatch(push(`/product-groups/${validationResult.groupId}`));
				}

				const errorLocation =
					dispatch(buildErrorLocation(validationResult.productErrors)) +
					formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

				if (error.handleRequiredProductSettingsMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
				}
				if (error.handleRequiresMinOneReferenceProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation));
				}
				if (error.handleRequiresMaxOneReferenceProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
				}
				if (error.handleRequiresMaxOneProductPerDataSource()) {
					dispatch(
						flashErrorMessage(downlaodFlashMessages.RequiresAtMostOneRomanianProductForEachDatasource + errorLocation)
					);
				}
				if (error.handleRequiresReferenceSettingsErrorCode()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
				}
				if (error.handleCalculationRequiresAtLeastOneProductList()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
				}
				if (error.handleExportLimitExceeded()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.LimitExceededMessage + errorLocation));
				}
				if (error.handleIllegalPriceTypeSelected()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
				}
				dispatch(handleRoMaxPriceUpdateResponse(error, props.group.id));
			}
		},
	};
};

function handleSkMaxPriceExport(
	createExport: (groupId: string) => ThunkAction<Promise<any>, AnyAction>,
	dispatch: ThunkDispatch<AnyAction>,
	props: { group: UserProductGroup }
): Promise<any> {
	dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductErrorMessage));
	dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
	dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));
	dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));

	return dispatch(commitSkMaxPriceUpdate(props.group.id))
		.then(() => dispatch(createExport(props.group.id)))
		.then((response) => dispatch(processExport(response.export)))
		.catch((response) => {
			if (!(response instanceof SkMaxPriceErrorResponse)) {
				dispatch(handleError(response));
				return;
			}

			const validationResult = response.toValidationResult();
			if (validationResult.groupId !== null) {
				dispatch(push(`/product-groups/${validationResult.groupId}`));
			}

			const errorLocation =
				dispatch(buildErrorLocation(validationResult.productErrors)) +
				formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

			if (response.handleCalculationRequiresAtLeastOneProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiresAtMostOneProductForEachCountry()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiresAtLeastOneNonSkProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingNonSlovakProductErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiredProductSettingsIsMissing()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
			}
			if (response.handleExportRequiresReferenceSettings()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
			}
			if (response.handleIllegalPriceTypeSelected()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiresAtLeastOneProductList()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
			}
			if (response.handleLpCnRefExportContainsSlovakPzluProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.LpCnRefExportContainsSlovakPzluProduct + errorLocation));
			}
			if (response.handlePzluCnRefExportContainsSlovakLpProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.PzluCnRefExportContainsSlovakLpProduct + errorLocation));
			}
			if (response.handleAtLeastOneReferenceCountryProduct()) {
				dispatch(
					flashErrorMessage(downlaodFlashMessages.ExportB1RequiresReferenceCountriesErrorMessage + errorLocation)
				);
			}

			dispatch(handleSkMaxPriceUpdateResponse(response, props.group.id));
		});
}

export default connect(mapStateToProps, mapDispatchToProps)(DownloadPopover);
